import { ArrowPathIcon, ChartBarIcon, CheckCircleIcon, DocumentTextIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
    Button,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Select,
    Option,
    Typography,
    IconButton,
    Card,
    CardBody,
    Chip
} from "@material-tailwind/react";
import { useState, useEffect } from "react";
import axios from "axios";

export default function DailyProfitCardFigure() {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [isLoading, setIsLoading] = useState(false);
    const [dailyProfits, setDailyProfits] = useState([]);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const toggleDialog = () => setIsDialogOpen(!isDialogOpen);
    const toggleReportDialog = () => {
        setIsReportDialogOpen(!isReportDialogOpen);
        if (isReportDialogOpen) {
            setIsDialogOpen(true);
        }
    };

    const closeReportDialog = () => {
        setIsReportDialogOpen(false);
    };

    const changePeriod = () => {
        setIsReportDialogOpen(false);
        setIsDialogOpen(true);
    };

    // Generate years from 2024 to current year
    const years = Array.from(
        { length: new Date().getFullYear() - 2023 },
        (_, i) => 2024 + i
    );

    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const fetchDailyProfits = async () => {
        setIsLoading(true);
        const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(
                `${API_URL}/figures/daily_profit`,
                {
                    params: {
                        year: selectedYear,
                        month: selectedMonth
                    },
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            setDailyProfits(response.data);
            setIsLoading(false);
            toggleDialog();
            toggleReportDialog();
        } catch (err) {
            console.error("Error fetching daily profits:", err);
            setIsLoading(false);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('sr-RS', {
            style: 'currency',
            currency: 'RSD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    const formatDateTime = (dateTime) => {
        const date = new Date(dateTime);
        const day = date.getDate();
        const month = date.toLocaleString('en-GB', { month: 'short' });
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');

        // Get day with ordinal suffix
        let dayWithSuffix;
        if (day > 3 && day < 21) {
            dayWithSuffix = `${day}th`;
        } else {
            switch (day % 10) {
                case 1: dayWithSuffix = `${day}st`; break;
                case 2: dayWithSuffix = `${day}nd`; break;
                case 3: dayWithSuffix = `${day}rd`; break;
                default: dayWithSuffix = `${day}th`;
            }
        }

        return `${dayWithSuffix} ${month}, ${hours}:${minutes}`;
    };

    useEffect(() => {
        const handleScroll = (e) => {
            if (isDialogOpen || isReportDialogOpen) {
                e.preventDefault();
                window.scrollTo(0, 0);
                return false;
            }
        };

        if (isDialogOpen || isReportDialogOpen) {
            document.body.classList.add('overflow-hidden', 'fixed', 'inset-0');
            document.addEventListener('touchmove', handleScroll, { passive: false });
        } else {
            document.body.classList.remove('overflow-hidden', 'fixed', 'inset-0');
            document.removeEventListener('touchmove', handleScroll);
        }

        return () => {
            document.body.classList.remove('overflow-hidden', 'fixed', 'inset-0');
            document.removeEventListener('touchmove', handleScroll);
        };
    }, [isDialogOpen, isReportDialogOpen]);


    return (
        <>
            <li className="py-3 sm:py-4 cursor-pointer" onClick={toggleDialog}>
                <div className="flex items-center space-x-4 text-left p-0 focus:bg-green-50 dark:focus:bg-cyan-900 hover:bg-green-50 dark:hover:bg-cyan-900">
                    <div className="flex-1 min-w-0">
                        <p className="text-md font-medium text-gray-900 truncate dark:text-white">
                            Daily Profit Breakdown
                        </p>
                        <div className="flex items-center space-x-2 mt-3">
                            <ChartBarIcon className="w-4 h-4 text-cyan-800 dark:text-cyan-200" />
                            <p className="text-sm text-gray-500">
                                View detailed daily transactions
                            </p>
                        </div>
                    </div>
                    <div className="ml-2">
                        <ChartBarIcon className="h-5 w-5 text-cyan-800 dark:text-cyan-200" />
                    </div>
                </div>
            </li>

            {/* Selection Dialog */}
            <Dialog size="md" open={isDialogOpen} handler={toggleDialog} className="p-4">
                <DialogHeader className="relative m-0 block">
                    <Typography variant="h4" color="blue-gray">
                        Daily Profit Breakdown
                    </Typography>
                    <Typography className="mt-1 font-normal text-gray-600">
                        Select year and month for the report
                    </Typography>
                    <IconButton
                        size="sm"
                        variant="text"
                        className="!absolute right-3.5 top-3.5"
                        onClick={toggleDialog}
                    >
                        <XMarkIcon className="h-4 w-4 stroke-2" />
                    </IconButton>
                </DialogHeader>

                <DialogBody className="space-y-4">
                    <div>
                        <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                            Year
                        </Typography>
                        <Select
                            label="Select Year"
                            value={selectedYear}
                            onChange={(value) => setSelectedYear(value)}
                        >
                            {years.map(year => (
                                <Option key={year} value={year}>
                                    {year}
                                </Option>
                            ))}
                        </Select>
                    </div>

                    <div>
                        <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                            Month
                        </Typography>
                        <Select
                            label="Select Month"
                            value={selectedMonth}
                            onChange={(value) => setSelectedMonth(value)}
                            displayValue={(value) => months[value - 1] || 'Select Month'}
                        >
                            {months.map((month, index) => (
                                <Option key={month} value={index + 1}>
                                    {month}
                                </Option>
                            ))}
                        </Select>
                    </div>
                </DialogBody>

                <DialogFooter>
                    <Button
                        variant="text"
                        color="gray"
                        onClick={toggleDialog}
                        className="mv-secondary-button mr-2"
                        disabled={isLoading}
                    >
                        <XMarkIcon className="h-4 w-4" />
                        Cancel
                    </Button>
                    <Button
                        className="mv-primary-button"
                        onClick={fetchDailyProfits}
                        disabled={isLoading}
                    >
                        {!isLoading && <DocumentTextIcon className="h-4 w-4" />}
                        {isLoading ? (
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : null}
                        {isLoading ? 'Generating...' : 'Generate'}
                    </Button>
                </DialogFooter>
            </Dialog>

            {/* Report Display Dialog */}
            <Dialog
                size={isMobile ? "md" : "xl"}
                open={isReportDialogOpen}
                handler={closeReportDialog}
                className="p-4 max-h-[calc(100vh-4rem)] overflow-y-auto"
            >
                <DialogHeader className="relative m-0 block">
                    <Typography variant="h4" color="blue-gray">
                        Daily Profit Breakdown
                    </Typography>
                    <Typography className="mt-1 font-normal text-gray-600">
                        {months[selectedMonth - 1]} {selectedYear}
                    </Typography>
                    <IconButton
                        size="sm"
                        variant="text"
                        className="!absolute right-3.5 top-3.5"
                        onClick={closeReportDialog}
                    >
                        <XMarkIcon className="h-4 w-4 stroke-2" />
                    </IconButton>
                </DialogHeader>

                <DialogBody className="space-y-6">
                    {dailyProfits.length > 0 ? (
                        isMobile ? (
                            <div className="space-y-3">
                                {dailyProfits.map((profit, index) => (
                                    <Card key={index} className="shadow-sm hover:shadow-md transition-shadow">
                                        <CardBody className="p-4">
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <Typography variant="small" className="font-bold text-blue-gray-800">
                                                        {formatDateTime(profit.transactionDateTime)}
                                                    </Typography>
                                                    <Typography variant="small" className="text-gray-700">
                                                        {profit.description}
                                                    </Typography>
                                                </div>
                                                <Chip
                                                    size="sm"
                                                    color={profit.type === 'MANGA_SALE' ? 'green' : 'red'}
                                                    value={profit.type === 'MANGA_SALE' ? 'Sale' : 'Expense'}
                                                    className="ml-2"
                                                />
                                            </div>

                                            <div className="flex justify-between items-center mt-3">
                                                <div>
                                                    <Typography variant="small" className="text-gray-600">
                                                        Amount:
                                                    </Typography>
                                                    <Typography variant="small" className={`font-medium ${profit.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                        {formatCurrency(profit.amount)}
                                                    </Typography>
                                                </div>

                                                <div className="text-right">
                                                    <Typography variant="small" className="text-gray-600">
                                                        Total:
                                                    </Typography>
                                                    <Typography variant="small" className={`font-medium ${profit.runningProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                        {formatCurrency(profit.runningProfit)}
                                                    </Typography>
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                ))}
                            </div>
                        ) : (
                            // Desktop prikaz (tabela)
                            <Card className="shadow-none">
                                <CardBody className="p-0">
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead>
                                            <tr className="border-b border-gray-200">
                                                <th className="text-left py-2 px-1">Date & Time</th>
                                                <th className="text-left py-2 px-1">Description</th>
                                                <th className="text-left py-2 px-1">Type</th>
                                                <th className="text-right py-2 px-1">Amount</th>
                                                <th className="text-right py-2 px-1">Total</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {dailyProfits.map((profit, index) => (
                                                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                                                    <td className="py-2 px-1">
                                                        <Typography variant="small" color="blue-gray">
                                                            {formatDateTime(profit.transactionDateTime)}
                                                        </Typography>
                                                    </td>
                                                    <td className="py-2 px-1">
                                                        <Typography variant="small">
                                                            {profit.description}
                                                        </Typography>
                                                    </td>
                                                    <td className="py-2 px-1">
                                                        <Chip
                                                            size="sm"
                                                            color={profit.type === 'FIGURE_SALE' ? 'green' : 'red'}
                                                            value={profit.type === 'FIGURE_SALE' ? 'Sale' : 'Expense'}
                                                            className="w-fit"
                                                        />
                                                    </td>
                                                    <td className={`py-2 px-1 text-right ${profit.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                        <Typography variant="small">
                                                            {formatCurrency(profit.amount)}
                                                        </Typography>
                                                    </td>
                                                    <td className={`py-2 px-1 text-right ${profit.runningProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                        <Typography variant="small">
                                                            {formatCurrency(profit.runningProfit)}
                                                        </Typography>
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </CardBody>
                            </Card>
                        )
                    ) : (
                        <Typography className="text-center py-4">
                            No data available for selected period
                        </Typography>
                    )}
                </DialogBody>

                <DialogFooter className="gap-2">
                    <Button
                        className="mv-secondary-button"
                        onClick={changePeriod}
                    >
                        <ArrowPathIcon className="h-4 w-4" />
                        Change period
                    </Button>
                    <Button
                        className="mv-primary-button"
                        onClick={closeReportDialog}
                    >
                        <CheckCircleIcon className="h-4 w-4" />
                        Done
                    </Button>
                </DialogFooter>
            </Dialog>
        </>
    );
}
